// Integration Types for Financial Systems

export type IntegrationType = 'access-dimensions' | 'access-financials' | 'xero' | 'quickbooks';

export type IntegrationStatus = 'connected' | 'disconnected' | 'error' | 'coming-soon';

export type EnvironmentType = 'production' | 'staging' | 'test' | 'development';

export interface DatabaseInstance {
  id: string;
  name: string;
  environment?: EnvironmentType;
  connectionStatus: 'active' | 'inactive' | 'error';
  lastSync?: string;
  description?: string;
}

export interface Integration {
  id: IntegrationType;
  name: string;
  description: string;
  status: IntegrationStatus;
  logo?: string;
  selectedInstanceId?: string;
  availableInstances?: DatabaseInstance[];
  comingSoon?: boolean;
  features?: string[];
}

export interface IntegrationConfig {
  activeIntegration?: IntegrationType;
  integrations: {
    'access-dimensions'?: {
      enabled: boolean;
      instanceId?: string;
      lastSync?: string;
    };
    'access-financials'?: {
      enabled: boolean;
      instanceId?: string;
      lastSync?: string;
    };
    xero?: {
      enabled: boolean;
      instanceId?: string;
      lastSync?: string;
    };
    quickbooks?: {
      enabled: boolean;
      instanceId?: string;
      lastSync?: string;
    };
  };
}

// Mock data for development
export const mockIntegrations: Integration[] = [
  {
    id: 'access-dimensions',
    name: 'Access Dimensions',
    description: 'Enterprise financial management system for comprehensive asset tracking and reporting',
    status: 'disconnected',
    logo: 'https://provisioning-preproderp.accessacloud.com/Provisioning/images/DimensionsSaas.png',
    features: [
      'Multi-dimensional reporting',
      'Advanced cost allocation',
      'Real-time financial sync',
      'Custom dimension mapping'
    ]
  },
  {
    id: 'access-financials',
    name: 'Access Financials',
    description: 'Core accounting and financial reporting platform for seamless data integration',
    status: 'disconnected',
    logo: 'https://preprodgo.accessacloud.com/content/images/icons/128/financials.png',
    features: [
      'General ledger integration',
      'Automated journal entries',
      'Financial statement sync',
      'Audit trail tracking'
    ]
  },
  {
    id: 'xero',
    name: 'Xero',
    description: 'Cloud-based accounting software for small to medium-sized businesses',
    status: 'coming-soon',
    comingSoon: true,
    logo: '/integrations/xero.svg',
    features: [
      'Cloud-based sync',
      'Invoice integration',
      'Bank reconciliation',
      'Real-time reporting'
    ]
  },
  {
    id: 'quickbooks',
    name: 'QuickBooks',
    description: 'Popular accounting software for small businesses with powerful asset management',
    status: 'coming-soon',
    comingSoon: true,
    logo: '/integrations/quickbooks.svg',
    features: [
      'Automated bookkeeping',
      'Expense tracking',
      'Financial reporting',
      'Tax preparation support'
    ]
  }
];

export const mockDatabaseInstances: Record<IntegrationType, DatabaseInstance[]> = {
  'access-dimensions': [
    {
      id: 'dim-prod-001',
      name: 'Access Dimensions Production',
      environment: 'production',
      connectionStatus: 'active',
      lastSync: '2024-01-15T10:30:00Z',
      description: 'Primary production database'
    },
    {
      id: 'dim-stage-001',
      name: 'Access Dimensions Staging',
      environment: 'staging',
      connectionStatus: 'active',
      lastSync: '2024-01-15T09:15:00Z',
      description: 'Staging environment for testing'
    },
    {
      id: 'dim-test-001',
      name: 'Access Dimensions Test',
      environment: 'test',
      connectionStatus: 'active',
      description: 'Test environment'
    }
  ],
  'access-financials': [
    {
      id: 'fin-prod-001',
      name: 'Access Financials Production',
      environment: 'production',
      connectionStatus: 'active',
      lastSync: '2024-01-15T11:00:00Z',
      description: 'Main financial system database'
    },
    {
      id: 'fin-dev-001',
      name: 'Access Financials Development',
      environment: 'development',
      connectionStatus: 'active',
      description: 'Development environment'
    }
  ],
  xero: [],
  quickbooks: []
};
